/**
 * SOVRANOVA — METABOLIC: calm eating + optional intermittent fasting.
 *
 * SAFETY-FIRST (load-bearing): not for minors; an eating-disorder-risk screen
 * gates entry (any concern → support, never fasting content); a medical
 * disclaimer is always shown; and every wave-reset ALWAYS offers "I'm going to
 * eat" as a good, encouraged choice. Nothing here ever tells you to override
 * real hunger or push through feeling unwell. No weight or calorie tracking.
 *
 * Gated by VITE_FEATURE_METABOLIC (frontend) + FEATURE_METABOLIC (backend).
 * Backend: /api/metabolic.
 */
import { useState, useEffect } from 'react'
import { api } from '../../services/api'

const TRIGGERS = ['Stress', 'Boredom', 'Habit', 'Sadness', 'Anxiety', 'Loneliness', 'None of these']
const SYMPTOMS = ['Dizzy / lightheaded', 'Shaky', 'Headache', 'Heart racing', 'Very weak', 'Nauseous', 'None']
const SYM_MAP = { 'Dizzy / lightheaded': 'dizzy', 'Shaky': 'shaky', 'Headache': 'headache', 'Heart racing': 'heart_racing', 'Very weak': 'very_weak', 'Nauseous': 'nauseous' }

// Gentle safety screen. ANY "yes" routes to support — this is not a diagnosis,
// it's a check so we only offer fasting tools to people they're safe for.
const SCREEN = [
  'Are you currently struggling with, in treatment for, or recovering from an eating disorder?',
  'Do you ever feel out of control with eating, or make yourself sick afterwards?',
  'Does skipping meals feel like relief from guilt, or a way to "earn" food?',
  'Are you pregnant, breastfeeding, under 18, or managing diabetes or another condition where fasting could be risky?',
]

const SUPPORT = { name: 'Butterfly Foundation (eating disorders, AU)', phone: '1800 33 4673', tel: '1800334673' }

export function MetabolicView({ onClose, onHome }) {
  const [config, setConfig] = useState(undefined)
  const [view, setView] = useState('home') // home | wave
  useEffect(() => {
    api.get('/metabolic/config').then(r => setConfig(r || { eligible: false, edScreen: 'not_taken' }))
      .catch(() => setConfig({ eligible: false, edScreen: 'not_taken' }))
  }, [])

  if (config === undefined) return <div style={s.page}><p style={s.help}>Loading…</p></div>

  if (config.isMinor) {
    return <Shell onClose={onClose}><Gate title="Not available" body="This space supports adults with eating and fasting. It isn't available on this account." onClose={onClose} /></Shell>
  }
  if (config.edScreen === 'flagged') {
    return <Shell onClose={onClose}><SupportCard /></Shell>
  }
  if (config.edScreen !== 'passed') {
    return <Shell onClose={onClose}><EdScreen onDone={(c) => setConfig({ ...config, ...c })} onClose={onClose} /></Shell>
  }

  return (
    <Shell onClose={onClose} onHome={onHome}>
      {view === 'home'
        ? <HomePanel onWave={() => setView('wave')} onFlagged={() => setConfig({ ...config, edScreen: 'flagged' })} />
        : <HungerWave onBack={() => setView('home')} onFlagged={() => setConfig({ ...config, edScreen: 'flagged' })} />}
    </Shell>
  )
}

function Shell({ children, onClose, onHome }) {
  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <button style={s.back} onClick={onClose}>← Back</button>
        {onHome && <button style={s.home} onClick={onHome}>⌂</button>}
      </div>
      {children}
    </div>
  )
}

function Gate({ title, body, onClose }) {
  return (
    <div style={s.gateWrap}>
      <h1 style={s.gateTitle}>{title}</h1>
      <p style={s.gateBody}>{body}</p>
      <button style={s.softBtn} onClick={onClose}>Back</button>
    </div>
  )
}

function SupportCard() {
  return (
    <div style={s.gateWrap}>
      <span style={s.heart}>💛</span>
      <h1 style={s.gateTitle}>Let's take fasting off the table</h1>
      <p style={s.gateBody}>From what you shared, a fasting tool isn't the right or safe thing for you right now — and that's not a failing, it's care. Your relationship with food deserves real, gentle support.</p>
      <a href={`tel:${SUPPORT.tel}`} style={s.callPrimary}><span>{SUPPORT.name.split(' (')[0]}</span><span style={s.callNum}>{SUPPORT.phone}</span></a>
      <a href="tel:131114" style={s.callSecondary}><span>Lifeline</span><span style={s.callNum}>13 11 14</span></a>
      <p style={s.region}>Australia · free &amp; confidential</p>
      <p style={s.help}>SOVRANOVA's other spaces — and a real practitioner — are here for you. You are not a problem to be fixed.</p>
    </div>
  )
}

// ── ED-risk safety screen ───────────────────────────────────────────────────
function EdScreen({ onDone, onClose }) {
  const [answers, setAnswers] = useState({})
  const [busy, setBusy] = useState(false)
  const allAnswered = SCREEN.every((_, i) => answers[i] !== undefined)
  const anyYes = SCREEN.some((_, i) => answers[i] === true)

  const submit = async () => {
    setBusy(true)
    try {
      await api.post('/metabolic/ed-screen', { flagged: anyYes })
      onDone(anyYes ? { edScreen: 'flagged' } : { eligible: true, edScreen: 'passed' })
    } catch { onDone(anyYes ? { edScreen: 'flagged' } : { eligible: true, edScreen: 'passed' }) }
    setBusy(false)
  }

  return (
    <div style={s.col}>
      <h2 style={s.panelH}>A quick safety check first</h2>
      <p style={s.help}>Fasting tools aren't safe for everyone. This isn't a test or a diagnosis — it just helps us make sure this space is right for you. Answer honestly; there are no wrong answers.</p>
      {SCREEN.map((q, i) => (
        <div key={i} style={s.qCard}>
          <p style={s.qText}>{q}</p>
          <div style={s.qBtns}>
            <button onClick={() => setAnswers(a => ({ ...a, [i]: false }))} style={{ ...s.qBtn, ...(answers[i] === false ? s.qBtnOn : {}) }}>No</button>
            <button onClick={() => setAnswers(a => ({ ...a, [i]: true }))} style={{ ...s.qBtn, ...(answers[i] === true ? s.qBtnYes : {}) }}>Yes</button>
          </div>
        </div>
      ))}
      <button style={s.primaryBtn} onClick={submit} disabled={!allAnswered || busy}>{busy ? '…' : 'Continue'}</button>
      <button style={s.softBtn} onClick={onClose}>Not now</button>
    </div>
  )
}

// ── Home: medical disclaimer + state + check-in ─────────────────────────────
function HomePanel({ onWave, onFlagged }) {
  const [hours, setHours] = useState(12)
  const [hunger, setHunger] = useState(5)
  const [trigger, setTrigger] = useState('None of these')
  const [symptoms, setSymptoms] = useState([])
  const [rec, setRec] = useState(null)
  const [busy, setBusy] = useState(false)

  const toggleSym = (label) => setSymptoms(prev => prev.includes(label) ? prev.filter(x => x !== label) : (label === 'None' ? ['None'] : [...prev.filter(x => x !== 'None'), label]))

  const evaluate = async () => {
    setBusy(true); setRec(null)
    const physicalSymptoms = symptoms.filter(x => x !== 'None').map(x => SYM_MAP[x]).filter(Boolean)
    const emotionalTrigger = trigger === 'None of these' ? 'none' : trigger.toLowerCase()
    try {
      const r = await api.post('/metabolic/evaluate', { fastingHours: Number(hours), perceivedHunger: hunger, emotionalTrigger, physicalSymptoms })
      setRec(r?.result || null)
    } catch { setRec(null) }
    setBusy(false)
  }

  return (
    <div style={s.col}>
      <header style={s.hero}>
        <p style={s.kicker}>SOVRANOVA · METABOLIC</p>
        <h1 style={s.h1}>A calm relationship with eating</h1>
      </header>
      <div style={s.disclaimer}>This is wellbeing support, not medical or nutritional advice. Fasting isn't right for everyone. Please check with your doctor first, and never ignore real hunger or feeling unwell — eating is always okay.</div>

      <div style={s.card}>
        <h3 style={s.cardTitle}>Where are you right now?</h3>
        <label style={s.fieldRow}><span style={s.fieldLabel}>Hours since you last ate</span>
          <input type="number" min="0" max="48" value={hours} onChange={e => setHours(e.target.value)} style={s.numInput} /></label>
        <label style={s.fieldRow}><span style={s.fieldLabel}>Hunger right now (0–10)</span>
          <input type="number" min="0" max="10" value={hunger} onChange={e => setHunger(Number(e.target.value))} style={s.numInput} /></label>
        <span style={s.fieldLabel}>What's underneath it?</span>
        <div style={s.chips}>{TRIGGERS.map(t => <button key={t} onClick={() => setTrigger(t)} style={{ ...s.chip, ...(trigger === t ? s.chipOn : {}) }}>{t}</button>)}</div>
        <span style={s.fieldLabel}>Any of these in your body?</span>
        <div style={s.chips}>{SYMPTOMS.map(t => <button key={t} onClick={() => toggleSym(t)} style={{ ...s.chip, ...(symptoms.includes(t) ? s.chipOn : {}) }}>{t}</button>)}</div>
        <button style={s.primaryBtn} onClick={evaluate} disabled={busy}>{busy ? '…' : 'How should I navigate this?'}</button>
      </div>

      {rec && <Intervention rec={rec} onWave={onWave} onFlagged={onFlagged} />}

      <button style={s.waveBtn} onClick={onWave}>🌊 Navigate the wave</button>
      <p style={s.foot}>Choosing to eat is never a failure here. If a tracking app or fasting ever starts to feel compulsive or distressing, please step back and reach out for support.</p>
    </div>
  )
}

function Intervention({ rec, onWave, onFlagged }) {
  const iv = rec.intervention
  if (iv.type === 'EAT_NOW') {
    return (
      <div style={{ ...s.recCard, borderColor: '#e8b04b' }}>
        <p style={s.recTitle}>{iv.title}</p>
        <p style={s.recMsg}>{iv.message}</p>
        {iv.support && <a href="tel:1800334673" style={s.callPrimary}><span>{iv.support.name.split(' (')[0]}</span><span style={s.callNum}>{iv.support.phone}</span></a>}
        {iv.support && <button style={s.softBtn} onClick={onFlagged}>Get ongoing support</button>}
      </div>
    )
  }
  if (iv.type === 'HUNGER_WAVE') {
    return (
      <div style={s.recCard}>
        <p style={s.recTitle}>{iv.title}</p>
        <p style={s.recMsg}>{iv.message}</p>
        <button style={s.primaryBtn} onClick={onWave}>Ride it with a breath →</button>
      </div>
    )
  }
  return (
    <div style={s.recCard}>
      <p style={s.recTitle}>{iv.title}</p>
      <p style={s.recMsg}>{iv.message}</p>
    </div>
  )
}

// ── The Hunger Wave Reset — minimalist 4-7-8 pacer ──────────────────────────
const ANCHORS = [
  'Waves crest, then they ease.',
  "You're safe. Nothing is wrong.",
  'Soften your shoulders, unclench your jaw.',
  'If this is real hunger, eating is wise too.',
]

function HungerWave({ onBack, onFlagged }) {
  const [phrase, setPhrase] = useState(0)
  const [stage, setStage] = useState('breathe') // breathe | ate | unwell
  useEffect(() => { const t = setInterval(() => setPhrase(p => (p + 1) % ANCHORS.length), 6000); return () => clearInterval(t) }, [])

  const outcome = async (ateInstead) => {
    try { await api.post('/metabolic/outcome', { ateInstead }) } catch { /* */ }
    setStage(ateInstead ? 'ate' : 'done')
  }

  if (stage === 'ate' || stage === 'done') {
    return (
      <div style={s.waveWrap}>
        <span style={s.heart}>🌿</span>
        <h2 style={s.gateTitle}>{stage === 'ate' ? 'Good. You listened to your body.' : 'Nicely done.'}</h2>
        <p style={s.gateBody}>{stage === 'ate' ? 'Eating when you need to is strength, not weakness. There is nothing to undo and nothing to earn back.' : 'You gave the wave a moment to pass. However the rest of today goes is okay.'}</p>
        <button style={s.softBtn} onClick={onBack}>← Back</button>
      </div>
    )
  }

  if (stage === 'unwell') {
    return (
      <div style={s.waveWrap}>
        <span style={s.heart}>💛</span>
        <h2 style={s.gateTitle}>Please eat something now</h2>
        <p style={s.gateBody}>Feeling unwell is your body asking for food — that comes first, always. Have something gentle. If it doesn't ease, contact your doctor or, in an emergency, call 000.</p>
        <a href="tel:131114" style={s.callSecondary}><span>Lifeline</span><span style={s.callNum}>13 11 14</span></a>
        <button style={s.softBtn} onClick={onBack}>← Back</button>
      </div>
    )
  }

  return (
    <div style={s.waveWrap}>
      <div style={s.breathStage}>
        <div style={s.breathCircle} />
      </div>
      <p style={s.breathCue}>Breathe with the circle — in as it grows, slow out as it shrinks.</p>
      <p style={s.anchor}>{ANCHORS[phrase]}</p>
      <div style={s.waveBtns}>
        <button style={s.primaryBtn} onClick={() => outcome(true)}>I'm going to eat — that's good</button>
        <button style={s.softBtn} onClick={() => outcome(false)}>I rode the wave</button>
        <button style={s.unwellBtn} onClick={() => setStage('unwell')}>I feel unwell</button>
      </div>
    </div>
  )
}

// ── Styles ──────────────────────────────────────────────────────────────────
const s = {
  page:    { minHeight:'100vh', background:'linear-gradient(170deg,#0d1512,#0f1a16,#0c1411)', padding:'1.1rem 1.1rem 2.5rem', display:'flex', flexDirection:'column', gap:'1rem', color:'#e6f0ec' },
  topbar:  { display:'flex', justifyContent:'space-between' },
  back:    { background:'transparent', border:'none', color:'rgba(230,240,236,.6)', fontSize:'0.9rem', cursor:'pointer' },
  home:    { background:'transparent', border:'none', color:'rgba(230,240,236,.6)', fontSize:'1.2rem', cursor:'pointer' },
  hero:    { display:'flex', flexDirection:'column', gap:'0.25rem' },
  kicker:  { fontSize:'0.64rem', letterSpacing:'0.16em', textTransform:'uppercase', color:'#5fc99a', fontWeight:700, margin:0 },
  h1:      { fontSize:'1.7rem', fontWeight:800, color:'#eef6f2', margin:0 },
  col:     { display:'flex', flexDirection:'column', gap:'0.8rem' },
  panelH:  { fontSize:'1.25rem', fontWeight:800, color:'#eef6f2', margin:0 },
  help:    { fontSize:'0.8rem', color:'rgba(230,240,236,.55)', lineHeight:1.55, margin:0 },
  disclaimer:{ fontSize:'0.76rem', color:'#dcebd0', background:'rgba(95,201,154,.08)', border:'1px solid rgba(95,201,154,.25)', borderRadius:'0.7rem', padding:'0.7rem 0.85rem', lineHeight:1.5 },
  card:    { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'1rem', padding:'1rem', display:'flex', flexDirection:'column', gap:'0.6rem' },
  cardTitle:{ fontSize:'1rem', fontWeight:700, margin:0 },
  fieldRow:{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.8rem' },
  fieldLabel:{ fontSize:'0.82rem', color:'rgba(230,240,236,.78)' },
  numInput:{ width:'4.5rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.14)', borderRadius:'0.6rem', padding:'0.5rem', color:'#eef6f2', fontSize:'0.9rem', fontFamily:'inherit', textAlign:'center' },
  chips:   { display:'flex', flexWrap:'wrap', gap:'0.4rem' },
  chip:    { background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.14)', color:'rgba(230,240,236,.7)', borderRadius:'999px', padding:'0.4rem 0.7rem', fontSize:'0.76rem', cursor:'pointer', fontFamily:'inherit' },
  chipOn:  { background:'rgba(95,201,154,.2)', border:'1px solid rgba(95,201,154,.55)', color:'#cdeede', fontWeight:600 },
  primaryBtn:{ marginTop:'0.2rem', padding:'0.8rem 1rem', width:'100%', background:'linear-gradient(135deg,#1e7a5a,#3fb98a)', color:'#fff', border:'none', borderRadius:'0.85rem', fontSize:'0.92rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit' },
  softBtn: { padding:'0.7rem 1rem', width:'100%', background:'rgba(255,255,255,.06)', color:'#dcebe4', border:'1px solid rgba(255,255,255,.16)', borderRadius:'0.8rem', fontSize:'0.88rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  waveBtn: { padding:'1rem', width:'100%', background:'rgba(95,201,154,.12)', color:'#bfe9d6', border:'1px solid rgba(95,201,154,.4)', borderRadius:'1rem', fontSize:'1rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit' },
  unwellBtn:{ padding:'0.65rem 1rem', width:'100%', background:'rgba(232,176,75,.12)', color:'#f0d49a', border:'1px solid rgba(232,176,75,.45)', borderRadius:'0.8rem', fontSize:'0.85rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  recCard: { background:'rgba(255,255,255,.05)', border:'1px solid rgba(95,201,154,.3)', borderRadius:'0.9rem', padding:'0.9rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  recTitle:{ fontSize:'0.95rem', fontWeight:800, color:'#9fe3c2', margin:0 },
  recMsg:  { fontSize:'0.88rem', color:'#e6f0ec', lineHeight:1.55, margin:0 },
  foot:    { fontSize:'0.74rem', color:'rgba(230,240,236,.4)', lineHeight:1.6, fontStyle:'italic', borderTop:'1px solid rgba(255,255,255,.08)', paddingTop:'0.9rem', margin:0 },
  qCard:   { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'0.8rem', padding:'0.8rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  qText:   { fontSize:'0.88rem', color:'#e6f0ec', lineHeight:1.5, margin:0 },
  qBtns:   { display:'flex', gap:'0.5rem' },
  qBtn:    { flex:1, padding:'0.5rem', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.14)', color:'rgba(230,240,236,.7)', borderRadius:'0.6rem', fontSize:'0.85rem', cursor:'pointer', fontFamily:'inherit' },
  qBtnOn:  { background:'rgba(95,201,154,.2)', border:'1px solid rgba(95,201,154,.55)', color:'#cdeede', fontWeight:700 },
  qBtnYes: { background:'rgba(232,176,75,.18)', border:'1px solid rgba(232,176,75,.55)', color:'#f0d49a', fontWeight:700 },
  gateWrap:{ display:'flex', flexDirection:'column', gap:'0.8rem', alignItems:'center', textAlign:'center', maxWidth:430, margin:'1.5rem auto 0' },
  gateTitle:{ fontSize:'1.5rem', fontWeight:800, color:'#eef6f2', margin:0 },
  gateBody:{ fontSize:'0.92rem', color:'rgba(230,240,236,.7)', lineHeight:1.6, margin:0 },
  heart:   { fontSize:'2.2rem' },
  callPrimary:{ display:'flex', justifyContent:'space-between', alignItems:'center', textDecoration:'none', padding:'0.7rem 0.85rem', borderRadius:'0.75rem', background:'rgba(95,201,154,.16)', border:'1px solid rgba(95,201,154,.5)', color:'#cdeede', fontWeight:700, width:'100%', boxSizing:'border-box' },
  callSecondary:{ display:'flex', justifyContent:'space-between', alignItems:'center', textDecoration:'none', padding:'0.7rem 0.85rem', borderRadius:'0.75rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.18)', color:'#e6f0ec', fontWeight:600, width:'100%', boxSizing:'border-box' },
  callNum: { fontSize:'1rem', fontWeight:800 },
  region:  { fontSize:'0.7rem', color:'rgba(230,240,236,.5)', textAlign:'center', margin:0 },
  waveWrap:{ display:'flex', flexDirection:'column', gap:'1rem', alignItems:'center', textAlign:'center', padding:'1.5rem 0.5rem' },
  breathStage:{ height:200, display:'flex', alignItems:'center', justifyContent:'center' },
  breathCircle:{ width:120, height:120, borderRadius:'50%', background:'radial-gradient(circle at 50% 40%, rgba(95,201,154,.5), rgba(95,201,154,.12))', border:'2px solid rgba(95,201,154,.5)', animation:'mbBreath 19s ease-in-out infinite' },
  breathCue:{ fontSize:'0.82rem', color:'rgba(230,240,236,.6)', margin:0, maxWidth:300, lineHeight:1.5 },
  anchor:  { fontSize:'1.05rem', color:'#cdeede', fontWeight:600, margin:0, minHeight:'1.6rem', transition:'opacity .4s' },
  waveBtns:{ display:'flex', flexDirection:'column', gap:'0.5rem', width:'100%', maxWidth:360, marginTop:'0.5rem' },
}

if (typeof document !== 'undefined' && !document.getElementById('mb-breath-anim')) {
  const el = document.createElement('style'); el.id = 'mb-breath-anim'
  el.textContent = '@keyframes mbBreath{0%{transform:scale(1)}21%{transform:scale(1.55)}58%{transform:scale(1.55)}100%{transform:scale(1)}}'
  document.head.appendChild(el)
}
